import { useRef, useState } from 'react';
import {
    Box,
    Button,
    FlexBox,
    Icon,
    IconButton,
    SelectionSet,
    SelectionSetInput,
    SelectionSetLabel,
    Typography,
} from '@vp/swan';
import {
    REFERENCE_PROJECTS_DECISION_WITH,
    REFERENCE_PROJECTS_DECISION_WITHOUT,
} from '../../types/selectors';
import { HelpMeAnswerButton } from '../HelpMeAnswerButton';
import { ReferenceProjectsPicker } from './LazyReferenceProjectsPicker';
import type { ReferenceProjectsData, ReferenceProjectsResult } from '../../types/selectors';

export interface ReferenceProjectsSelectorProps {
    data: ReferenceProjectsData;
    onComplete: (result: ReferenceProjectsResult) => void;
}

export function ReferenceProjectsSelector({ data, onComplete }: ReferenceProjectsSelectorProps) {
    // Auto-advance to step 2 if redirected back from sign-in with withReferenceProjects=1
    const shouldAutoAdvance = () => {
        try {
            const params = new URLSearchParams(window.location.search);
            return params.get('withReferenceProjects') === '1';
        } catch {
            return false;
        }
    };

    const autoAdvance = shouldAutoAdvance();
    const hasPreselectedWorkIds = (data.preselectedWorkIds?.length ?? 0) > 0;
    const startAtStep2 =
        autoAdvance ||
        hasPreselectedWorkIds ||
        data.preselectedDecision === REFERENCE_PROJECTS_DECISION_WITH;
    const [currentStep, setCurrentStep] = useState<1 | 2>(startAtStep2 ? 2 : 1);
    const [selectedValue, setSelectedValue] = useState<string | null>(
        startAtStep2 ? REFERENCE_PROJECTS_DECISION_WITH : data.preselectedDecision ?? null
    );
    const [selectedWorkIds, setSelectedWorkIds] = useState<string[]>(data.preselectedWorkIds ?? []);
    const stepHeadingRef = useRef<HTMLElement>(null);
    const [prevStep, setPrevStep] = useState(startAtStep2 ? 2 : 1);

    if (currentStep !== prevStep) {
        setPrevStep(currentStep);
        setTimeout(() => stepHeadingRef.current?.focus(), 0);
    }

    const handleSelectionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedValue(event.target.value);
    };

    const handleContinue = () => {
        if (!selectedValue) return;

        if (selectedValue === REFERENCE_PROJECTS_DECISION_WITHOUT) {
            onComplete({ decision: REFERENCE_PROJECTS_DECISION_WITHOUT });
        } else if (selectedValue === REFERENCE_PROJECTS_DECISION_WITH) {
            setCurrentStep(2);
        }
    };

    const totalSteps = selectedValue === REFERENCE_PROJECTS_DECISION_WITH ? 2 : 1;
    const showStepCounter = totalSteps > 1;
    const showChevrons = totalSteps > 1;

    return (
        <Box
            style={{
                backgroundColor: 'var(--swan-sem-color-bg-active)',
                border: '1px solid var(--swan-sem-color-border-standard)',
                padding: 'var(--swan-sem-space-6) var(--swan-sem-space-6) var(--swan-sem-space-4)',
                borderRadius:
                    'var(--swan-sem-border-radius-strong) var(--swan-sem-border-radius-strong) 0 0',
            }}
        >
            {/* Header: Title + Pagination */}
            <FlexBox
                justifyContent="space-between"
                alignItems="center"
                style={{
                    minHeight: '50px',
                    marginBottom: 'var(--swan-sem-space-5)',
                }}
            >
                <Typography
                    ref={stepHeadingRef}
                    tabIndex={-1}
                    fontSkin="title-item"
                    style={{ outline: 'none' }}
                >
                    {currentStep === 1
                        ? data.title || 'Would you like to reference existing projects?'
                        : 'Select from your Vistaprint projects to guide your designer\u2019s work'}
                </Typography>
                <FlexBox alignItems="center" gap={2}>
                    {showChevrons && (
                        <IconButton
                            skin="tertiary"
                            onClick={() => setCurrentStep(1)}
                            disabled={currentStep === 1}
                            aria-label="Previous step"
                            tooltip="Previous step"
                        >
                            <Icon iconType="chevronLeft" />
                        </IconButton>
                    )}
                    {showStepCounter && (
                        <Typography
                            fontSkin="body-small-bold"
                            display={{ xs: 'none', sm: 'block' }}
                        >
                            {currentStep} of {totalSteps}
                        </Typography>
                    )}
                    {showChevrons && (
                        <IconButton
                            skin="tertiary"
                            onClick={() => setCurrentStep(2)}
                            disabled={currentStep === 2}
                            aria-label="Next step"
                            tooltip="Next step"
                        >
                            <Icon iconType="chevronRight" />
                        </IconButton>
                    )}
                </FlexBox>
            </FlexBox>

            {/* Step 1: Decision */}
            {currentStep === 1 && (
                <>
                    <SelectionSet
                        variant="single-select"
                        selectedValue={selectedValue ?? undefined}
                        onChange={handleSelectionChange}
                        aria-label="Reference projects selection"
                        style={{ gap: 'var(--swan-sem-space-3)' }}
                    >
                        <SelectionSetInput value={REFERENCE_PROJECTS_DECISION_WITHOUT}>
                            <SelectionSetLabel
                                style={{
                                    padding: 0,
                                    ...(selectedValue !== REFERENCE_PROJECTS_DECISION_WITHOUT && {
                                        backgroundColor: 'var(--swan-sem-color-bg-standard)',
                                    }),
                                }}
                            >
                                <FlexBox
                                    as="span"
                                    alignItems="center"
                                    gap={3}
                                    style={{
                                        padding: 'var(--swan-sem-space-6) var(--swan-sem-space-5)',
                                    }}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            stroke="currentColor"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="m9.5 10.5 5 5m0-5-5 5M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2z"
                                        />
                                    </svg>
                                    <Typography fontSkin="body-standard-bold" as="span">
                                        No, continue without
                                    </Typography>
                                </FlexBox>
                            </SelectionSetLabel>
                        </SelectionSetInput>

                        <SelectionSetInput value={REFERENCE_PROJECTS_DECISION_WITH}>
                            <SelectionSetLabel
                                style={{
                                    padding: 0,
                                    ...(selectedValue !== REFERENCE_PROJECTS_DECISION_WITH && {
                                        backgroundColor: 'var(--swan-sem-color-bg-standard)',
                                    }),
                                }}
                            >
                                <FlexBox
                                    as="span"
                                    alignItems="center"
                                    gap={3}
                                    style={{
                                        padding: 'var(--swan-sem-space-6) var(--swan-sem-space-5)',
                                    }}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            stroke="currentColor"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20m0 0a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2z"
                                        />
                                    </svg>
                                    <Typography fontSkin="body-standard-bold" as="span">
                                        Yes, select projects
                                    </Typography>
                                </FlexBox>
                            </SelectionSetLabel>
                        </SelectionSetInput>
                    </SelectionSet>

                    {/* Footer: Actions */}
                    <FlexBox
                        justifyContent="space-between"
                        alignItems="center"
                        gap={3}
                        style={{
                            marginTop: 'var(--swan-sem-space-5)',
                        }}
                    >
                        <HelpMeAnswerButton selectorType="reference-projects" />
                        <Button skin="secondary" onClick={handleContinue} disabled={!selectedValue}>
                            Next
                        </Button>
                    </FlexBox>
                </>
            )}

            {/* Step 2: Project picker */}
            {currentStep === 2 && (
                <>
                    <ReferenceProjectsPicker
                        onSelectionChange={setSelectedWorkIds}
                        initialWorkIds={data.preselectedWorkIds}
                    />
                    {/* Footer: Actions */}
                    <FlexBox
                        justifyContent="flex-end"
                        alignItems="center"
                        gap={3}
                        style={{
                            marginTop: 'var(--swan-sem-space-5)',
                        }}
                    >
                        <Button
                            skin="secondary"
                            onClick={() =>
                                onComplete({
                                    decision: REFERENCE_PROJECTS_DECISION_WITH,
                                    workIds: selectedWorkIds,
                                })
                            }
                            disabled={selectedWorkIds.length === 0}
                        >
                            Continue
                        </Button>
                    </FlexBox>
                </>
            )}
        </Box>
    );
}
